import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface OrgProfileOrganization {
  id: string;
  name: string;
  logo_url: string | null;
}

export interface OrgProfileService {
  id: string;
  name: string;
  description: string | null;
  price: string | number;
  duration: string;
  category: string | null;
}

export interface OrgProfileBusinessHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

export interface OrgProfileData {
  organization: OrgProfileOrganization;
  services: OrgProfileService[];
  businessHours: OrgProfileBusinessHour[];
  isSuspended: boolean;
}

/**
 * Loads the public organisation profile (org + active services + business hours)
 * for the org's public-facing page, keyed by slug.
 *
 * Returns null when the slug resolves to nothing (not-found case).
 */
export function useOrgProfile(slug: string | undefined) {
  return useQuery({
    queryKey: ['org-profile', slug],
    enabled: Boolean(slug),
    queryFn: async (): Promise<OrgProfileData | null> => {
      // Resolve the org via the public SECURITY DEFINER RPC (same path as
      // OrgContext's subdomain resolution). A raw anon select on
      // `organizations` is blocked by RLS, which made /org/:slug render
      // "Business Not Found" on the apex domain.
      const { data: orgRows, error: orgError } = await supabase.rpc('get_org_by_slug', {
        _slug: slug!,
      });

      if (orgError) throw orgError;
      const orgRow = Array.isArray(orgRows) ? orgRows[0] : orgRows;
      if (!orgRow) return null;

      const orgData: OrgProfileOrganization = {
        id: orgRow.id,
        name: orgRow.name,
        logo_url: orgRow.logo_url,
      };

      const { data: isSuspended, error: suspendedError } = await supabase.rpc('is_org_suspended', {
        _org_id: orgData.id,
      });

      if (suspendedError) throw suspendedError;

      if (isSuspended) {
        return {
          organization: orgData,
          services: [],
          businessHours: [],
          isSuspended: true,
        };
      }

      const [servicesResult, hoursResult] = await Promise.all([
        supabase
          .from('services')
          .select('id, name, description, price, duration, category')
          .eq('organization_id', orgData.id)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('business_hours')
          .select('day_of_week, is_open, open_time, close_time')
          .eq('organization_id', orgData.id)
          .order('day_of_week', { ascending: true }),
      ]);

      if (servicesResult.error) throw servicesResult.error;
      if (hoursResult.error) throw hoursResult.error;

      return {
        organization: orgData,
        services: servicesResult.data ?? [],
        businessHours: hoursResult.data ?? [],
        isSuspended: false,
      };
    },
  });
}

interface EnquiryRequestResult {
  enquiryId?: string;
}

type CustomerEnquiryRpc = {
  rpc(
    name: 'submit_customer_enquiry',
    args: Record<string, string | null>
  ): Promise<{ data: EnquiryRequestResult | null; error: unknown }>;
};

export interface SubmitEnquiryInput {
  organizationId: string;
  serviceId: string;
  serviceName: string;
  clientName: string;
  clientEmail: string;
  address: string;
  additionalInfo?: string | null;
}

/**
 * Submits a booking enquiry via the `submit_customer_enquiry` RPC (same path as
 * Booking.tsx). On success sends confirmation and owner-notification emails
 * (non-blocking — a failed email does not fail the submission).
 */
export function useSubmitEnquiry() {
  return useMutation({
    mutationFn: async (input: SubmitEnquiryInput): Promise<EnquiryRequestResult | null> => {
      const { data: result, error } = await (
        supabase as unknown as CustomerEnquiryRpc
      ).rpc('submit_customer_enquiry', {
        _organization_id: input.organizationId,
        _name: input.clientName.trim(),
        _email: input.clientEmail.trim(),
        _phone: null,
        _address: input.address.trim(),
        _service_id: input.serviceId,
        _frequency: 'one-off',
        _premises_size: 'N/A',
        _subject: `Booking request: ${input.serviceName}`,
        _message: input.additionalInfo?.trim() || null,
      });

      if (error) throw error;

      // Non-blocking emails — failures must not fail the submission.
      try {
        await supabase.functions.invoke('send-enquiry-confirmation', {
          body: {
            recordId: result?.enquiryId,
            source: 'enquiry',
            organizationId: input.organizationId,
          },
        });
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      try {
        await supabase.functions.invoke('send-owner-notification', {
          body: {
            recordId: result?.enquiryId,
            source: 'enquiry',
            organizationId: input.organizationId,
          },
        });
      } catch (notifyError) {
        console.error('Owner notification failed:', notifyError);
      }

      return result;
    },
  });
}
